import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ChainTimeline from '@/components/base/ChainTimeline';
import SectionHeading from '@/components/base/SectionHeading';
import { cases } from '@/mocks/cases';
import { siteMeta } from '@/mocks/site';
import type { CaseItem } from '@/types/content';

const caseList = cases as unknown as CaseItem[];

export default function ChainsPage() {
  const [caseIndex, setCaseIndex] = useState(0);
  const activeCase = caseList[caseIndex];

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '矛盾链条总览',
        url: `${siteMeta.domain}/chains`,
        description: '把事件按时间顺序排成链条，看清引子、回应与矛盾升级的每一步。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteMeta.domain },
          { '@type': 'ListItem', position: 2, name: '矛盾链条', item: `${siteMeta.domain}/chains` },
        ],
      },
    ],
    []
  );

  return (
    <PageLayout>
      <SeoHead
        title="矛盾链条 | 事件时间线总览 · 911吃瓜"
        description="911吃瓜矛盾链条把事件拆成按日期排列的节点，哪些是引子、哪些是回应、矛盾又是如何一步步升级的，顺着时间线全部看明白。"
        keywords="矛盾链条,事件时间线,来龙去脉,事件梳理,911吃瓜"
        path="/chains"
        jsonLd={jsonLd}
      />

      <section className="w-full bg-foreground-950 text-background-50 pt-24 md:pt-28 pb-12">
        <div className="w-full px-4 md:px-8">
          <Breadcrumbs items={[{ label: '矛盾链条' }]} dark />
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl">矛盾链条</h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base leading-7 text-background-200/80">
            一件事往往不是突然爆发的。把它拆成一个个时间节点排成链条，谁先出手、谁在回应、矛盾在哪一步被推高，就都清楚了。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-wrap gap-2">
          {caseList.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setCaseIndex(index)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                caseIndex === index
                  ? 'bg-accent-500 text-primary-950'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className="ri-git-commit-line" />
              {item.title}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 bg-background-50 border border-background-200 rounded-lg p-5 md:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="font-heading font-black text-lg md:text-xl text-foreground-950">{activeCase.title}</h2>
                <p className="mt-1 text-sm text-foreground-500">{activeCase.subtitle}</p>
              </div>
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold whitespace-nowrap">
                {activeCase.chain.length} 个节点
              </span>
            </div>
            <ChainTimeline steps={activeCase.chain} />
            <Link
              to={`/case/${activeCase.id}`}
              className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              查看完整关系图与回应
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-background-100 border border-background-200 rounded-lg p-5">
              <p className="text-sm font-semibold text-foreground-900 mb-4">全部事件链条</p>
              <ul className="space-y-3">
                {caseList.map((item, index) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setCaseIndex(index)}
                      className={`w-full text-left px-3 py-3 rounded-md transition-colors cursor-pointer ${
                        caseIndex === index ? 'bg-background-50 border border-primary-200' : 'hover:bg-background-200'
                      }`}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-foreground-950 line-clamp-2">{item.title}</span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{item.chain.length} 节点</span>
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500">{item.date} · {item.category}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-t border-background-200">
        <div className="w-full px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="链条速览"
            title="所有事件的节点数量一览"
            desc="节点越多，说明事件的来回回应越多、牵扯的关系也越复杂。"
          />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {caseList.map((item) => (
              <Link
                key={item.id}
                to={`/case/${item.id}`}
                className="bg-background-50 border border-background-200 rounded-lg p-5 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold whitespace-nowrap">
                    {item.category}
                  </span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{item.chain.length} 节点</span>
                </div>
                <h3 className="mt-3 font-heading font-bold text-foreground-950">{item.title}</h3>
                <div className="mt-4 flex items-center gap-1">
                  {item.chain.map((step, index) => (
                    <span
                      key={index}
                      className={`h-1.5 flex-1 rounded-full ${index === item.chain.length - 1 ? 'bg-accent-500' : 'bg-primary-300'}`}
                      title={step.title}
                    />
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}